import React, { useState } from 'react';
import type { HouseholdMember } from '../api/bookingApi';

// Household hub (operaitor/backend#2277, option B): one link per phone resolves to
// every household member with open forms; the person picks who they're filling for
// before entering that member's own package. Identity shown is first-name-only by
// decision — no DOB or masked phone in the bearer payload.

export function HouseholdHub({ members, onSelect }: {
  members: HouseholdMember[];
  onSelect: (member: HouseholdMember) => void;
}) {
  return (
    <div className="py-4">
      <div className="text-center max-w-md mx-auto">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
          Welcome to our practice!
        </h2>
        <p className="text-gray-500 mt-3">
          There are multiple patients under this phone number. Please select the family
          member for whom you wish to fill out forms.
        </p>
        <p className="text-xs text-gray-500 mt-3">
          Your answers will be encrypted and sent securely to the office.
        </p>
      </div>

      <div className="mt-8 space-y-4 max-w-md mx-auto">
        {members.map((member) => (
          <MemberCard key={member.short_code} member={member} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

function MemberCard({ member, onSelect }: {
  member: HouseholdMember;
  onSelect: (member: HouseholdMember) => void;
}) {
  const [open, setOpen] = useState(false);
  const count = member.forms_count;

  return (
    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="w-11 h-11 rounded-full bg-primary-deep/10 text-primary-deep font-bold flex items-center justify-center shrink-0">
          {initials(member.first_name)}
        </div>
        <span className="min-w-0 flex-1 break-words text-base font-bold text-gray-900">
          {member.first_name}
          {member.is_minor && (
            <span className="ml-2 align-middle rounded-full bg-primary-deep/10 px-2 py-0.5 text-xs font-semibold text-primary-deep">
              Child
            </span>
          )}
        </span>
        <button
          data-testid="household-start"
          onClick={() => onSelect(member)}
          className="btn-primary shrink-0 max-w-[120px] bg-primary-deep hover:bg-primary-deeper"
        >
          Start
        </button>
      </div>
      <div className="border-t border-gray-100 bg-gray-50/70">
        <button
          type="button"
          data-testid="household-forms-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full flex items-center justify-between gap-2 px-5 py-3 text-left"
        >
          <span className="text-sm font-semibold text-gray-700">
            This patient has {count} {count === 1 ? 'form' : 'forms'} to fill
          </span>
          <svg
            className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {open && count > 0 && (
          <ul data-testid="household-forms-list" className="px-5 pb-3 space-y-1.5">
            {Array.from({ length: count }, (_, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-deep/50 shrink-0" />
                Form {i + 1}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// First-name-only, so a single initial (no surname to draw from).
function initials(firstName: string): string {
  return (firstName.trim()[0] ?? '?').toUpperCase();
}
